import { Plus, Camera, FileText, Mic, Coffee, Utensils, Calendar, Dumbbell } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function Home() {
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Привіт, Вадим! 👋</h1>
          <p className="text-slate-400 text-sm mt-0.5">Ось що відбувається сьогодні</p>
        </div>
        <div className="text-sm text-slate-400">
          Сьогодні · 15 травня, 2026
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Бюджет на місяць"
          value="₴ 24 500"
          sub="з ₴ 40 000"
          progress={61}
          color="violet"
        />
        <StatCard
          title="Витрати за сьогодні"
          value="₴ 680"
          sub="з ₴ 1 200"
          progress={57}
          color="emerald"
        />
        <StatCard
          title="Калорії за сьогодні"
          value="1 650 / 2 200"
          sub="ккал"
          progress={75}
          color="sky"
        />
        <StatCard
          title="Вода"
          value="1.4 / 2 л"
          sub="літрів"
          progress={70}
          color="cyan"
        />
      </div>

      {/* Quick actions */}
      <section>
        <h2 className="text-sm font-medium text-slate-400 mb-3">Швидкі дії</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <QuickAction
            to="/finances"
            icon={<Plus size={22} />}
            title="Додати витрату"
            subtitle="Записати нову витрату"
            gradient="from-violet-600 to-violet-800"
          />
          <QuickAction
            to="/nutrition"
            icon={<Camera size={22} />}
            title="Розпізнати їжу"
            subtitle="Порахувати калорії"
            gradient="from-emerald-600 to-emerald-800"
          />
          <QuickAction
            to="/plans"
            icon={<FileText size={22} />}
            title="Створити план"
            subtitle="Записати план на день"
            gradient="from-blue-600 to-blue-800"
          />
          <QuickAction
            to="/assistant"
            icon={<Mic size={22} />}
            title="Записати голосом"
            subtitle="Голосова команда"
            gradient="from-orange-600 to-orange-800"
          />
        </div>
      </section>

      {/* Recent activity + side panels */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Recent */}
        <div className="lg:col-span-2 card p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-4">Останні активності</h2>
          <div className="space-y-3">
            <ActivityItem
              icon={<Coffee size={16} className="text-amber-400" />}
              title="Кава та десерт"
              type="Витрата"
              value="- ₴ 95"
              time="сьогодні, 10:42"
            />
            <ActivityItem
              icon={<Utensils size={16} className="text-emerald-400" />}
              title="Куряче філе з рисом"
              type="Їжа"
              value="550 ккал"
              time="сьогодні, 09:15"
            />
            <ActivityItem
              icon={<Calendar size={16} className="text-blue-400" />}
              title="Зустріч з Андрієм"
              type="Подія"
              value=""
              time="сьогодні, 14:00"
            />
            <ActivityItem
              icon={<Dumbbell size={16} className="text-violet-400" />}
              title="План: Тренування"
              type="План"
              value=""
              time="сьогодні"
            />
          </div>
        </div>

        {/* Plans for today */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-slate-400">Плани на день</h2>
            <Link to="/plans" className="text-xs text-violet-400 hover:text-violet-300">
              Детальніше →
            </Link>
          </div>
          <div className="space-y-2.5">
            <PlanItem done title="Тренування в залі" time="08:00" />
            <PlanItem done title="Зустріч з клієнтом" time="11:00" />
            <PlanItem title="Підготувати звіт" time="15:00" />
            <PlanItem title="Почитати книгу" time="21:00" />
          </div>
        </div>
      </div>

      {/* Bottom input (chat-like) */}
      <div className="card p-3 flex items-center gap-3">
        <input
          type="text"
          placeholder="Напиши або скажи щось..."
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-500"
        />
        <button className="p-2 rounded-full bg-slate-700/50 hover:bg-slate-700 transition-colors">
          <Camera size={18} />
        </button>
        <button className="p-2.5 rounded-full bg-violet-600 hover:bg-violet-500 transition-colors">
          <Mic size={18} />
        </button>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  sub,
  progress,
  color,
}: {
  title: string
  value: string
  sub: string
  progress: number
  color: string
}) {
  const colors: Record<string, string> = {
    violet: 'bg-violet-500',
    emerald: 'bg-emerald-500',
    sky: 'bg-sky-500',
    cyan: 'bg-cyan-500',
  }
  return (
    <div className="card p-4">
      <div className="text-xs text-slate-400 mb-1">{title}</div>
      <div className="text-xl font-semibold tracking-tight">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
      <div className="mt-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${colors[color] || 'bg-violet-500'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="text-[10px] text-slate-500 mt-1 text-right">{progress}%</div>
    </div>
  )
}

function QuickAction({
  to,
  icon,
  title,
  subtitle,
  gradient,
}: {
  to: string
  icon: React.ReactNode
  title: string
  subtitle: string
  gradient: string
}) {
  return (
    <Link
      to={to}
      className={`bg-gradient-to-br ${gradient} rounded-2xl p-4 flex flex-col gap-2 hover:opacity-90 transition-opacity`}
    >
      <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-white/70 mt-0.5">{subtitle}</div>
      </div>
    </Link>
  )
}

function ActivityItem({
  icon,
  title,
  type,
  value,
  time,
}: {
  icon: React.ReactNode
  title: string
  type: string
  value: string
  time: string
}) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-8 h-8 rounded-lg bg-slate-800/80 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-xs text-slate-500">{type}</div>
      </div>
      <div className="text-right shrink-0">
        {value && <div className="text-sm font-medium">{value}</div>}
        <div className="text-xs text-slate-500">{time}</div>
      </div>
    </div>
  )
}

function PlanItem({ title, time, done }: { title: string; time: string; done?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <div
        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
          done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600'
        }`}
      >
        {done && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
      </div>
      <span className={`text-sm flex-1 ${done ? 'text-slate-500 line-through' : ''}`}>{title}</span>
      <span className="text-xs text-slate-500">{time}</span>
    </div>
  )
}
